import React from 'react';
import c from './layout.module.css';

/**
 * Standard page frame: a max-width column with a title block, optional
 * subtitle, optional right-hand actions and optional stepper.
 *
 * Pages used to centre a small element in the middle of a very wide viewport,
 * which is what made most screens look empty. Constraining the column and
 * anchoring content to a real page title fixes that everywhere it is used.
 *
 *   <PageLayout title="New Test" subtitle="Paste one SMILES per line.">
 *       ...
 *   </PageLayout>
 */
const PageLayout = ({title, subtitle, actions, steps, currentStep, narrow, children}) => (
    <div className={`${c.page}${narrow ? ' ' + c.pageNarrow : ''}`}>
        {(title || actions) && (
            <div className={c.pageHeader}>
                <div className={c.pageHeadings}>
                    {title && <h1 className={c.pageTitle}>{title}</h1>}
                    {subtitle && <p className={c.pageSubtitle}>{subtitle}</p>}
                </div>
                {actions && <div className={c.pageActions}>{actions}</div>}
            </div>
        )}
        {steps && <Stepper steps={steps} current={currentStep}/>}
        <div className={c.pageBody}>{children}</div>
    </div>
);

/**
 * Progress through a multi-step flow. `current` is the zero-based index of the
 * step being shown.
 */
export const Stepper = ({steps, current = 0}) => (
    <ol className={c.stepper}>
        {steps.map((label, i) => {
            const state = i < current ? c.stepDone : i === current ? c.stepCurrent : '';
            return (
                <React.Fragment key={label}>
                    {i > 0 && <li className={c.stepBar} aria-hidden="true"/>}
                    <li className={`${c.step} ${state}`}
                        aria-current={i === current ? 'step' : undefined}>
                        <span className={c.stepMark}>{i < current ? '✓' : i + 1}</span>
                        <span>{label}</span>
                    </li>
                </React.Fragment>
            );
        })}
    </ol>
);

/** Surface for grouping related content. `padded` for simple bodies, or compose
 *  Card + CardHeader + CardBody when the card needs a title. */
export const Card = ({padded = true, className = '', children}) => (
    <div className={`${c.card}${padded ? ' ' + c.cardPadded : ''}${className ? ' ' + className : ''}`}>
        {children}
    </div>
);

export const CardHeader = ({title, actions}) => (
    <div className={c.cardHeader}>
        <h2 className={c.cardTitle}>{title}</h2>
        {actions}
    </div>
);

export const CardBody = ({children}) => <div className={c.cardBody}>{children}</div>;

/** The four steps of the prediction flow, shared by the pages that make it up. */
export const PREDICT_STEPS = ['Compounds', 'Endpoints', 'Validation', 'Submit'];

export default PageLayout;
